import { createColumnHelper } from '@tanstack/table-core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { match } from 'ts-pattern';

import { NativeTable } from '@/components/nativeTable/nativeTable';
import { useNativeTable } from '@/components/nativeTable/useNativeTable';
import { useAgentsQuery } from '@/hooks/queries/useAgents';
import { useEvaluationsGet } from '@/hooks/queries/useEvaluations';
import { useSecretsQuery } from '@/hooks/queries/useSecrets';
import { ResourceKind } from '@/types/apiKey';

import styles from './apiKeysPanel.module.scss';


const PAGE_SIZE = 8;
const BACKEND_LIMIT = 500;  // Backend max limit - load all at once

type Option = { id: string; label: string };

const columnHelper = createColumnHelper<Option>();

// Debounce helper
function useDebounce<T>( value: T, delayMs: number ): T {
  const [ debouncedValue, setDebouncedValue ] = useState( value );

  useEffect( () => {
    const timer = setTimeout( () => setDebouncedValue( value ), delayMs );
    return () => clearTimeout( timer );
  }, [ value, delayMs ] );

  return debouncedValue;
}

function useResourceOptions( kind: ResourceKind, searchQuery: string ): {
  options: Option[];
  isLoading: boolean;
  hasMore: boolean;
} {
  const agents = useAgentsQuery( { enabled: kind === 'constellation' } );
  const secrets = useSecretsQuery( { enabled: kind === 'secret' } );

  // Server-side search with 500 limit for evaluations
  const debouncedSearch = useDebounce( searchQuery, 300 );
  const evaluations = useEvaluationsGet(
    {
      name: debouncedSearch || undefined,
      limit: BACKEND_LIMIT
    },
    { enabled: kind === 'evaluation' }
  );

  return useMemo(
    () => match( kind )
      .with( 'constellation', () => {
        const allOptions = ( agents.data ?? [] ).map( a => ( {
          id: a.id,
          label: a.constellationName ?? a.name ?? a.id
        } ) );
        const filtered = searchQuery.trim()
          ? allOptions.filter( o => o.label.toLowerCase().includes( searchQuery.toLowerCase() ) )
          : allOptions;
        return {
          options: filtered,
          isLoading: agents.isLoading,
          hasMore: false
        };
      } )
      .with( 'secret', () => {
        const allOptions = ( secrets.data ?? [] ).map( s => ( { id: s.id, label: s.name } ) );
        const filtered = searchQuery.trim()
          ? allOptions.filter( o => o.label.toLowerCase().includes( searchQuery.toLowerCase() ) )
          : allOptions;
        return {
          options: filtered,
          isLoading: secrets.isLoading,
          hasMore: false
        };
      } )
      .with( 'evaluation', () => ( {
        options: ( evaluations.data?.items ?? [] ).map( e => ( {
          id: e.evaluation.id,
          label: e.evaluation.name
        } ) ),
        isLoading: evaluations.isLoading,
        hasMore: evaluations.data?.pagination.has_more ?? false
      } ) )
      .with( 'api_key', () => ( {
        options: [],
        isLoading: false,
        hasMore: false
      } ) )
      .exhaustive(),
    [
      kind,
      searchQuery,
      agents.data, agents.isLoading,
      secrets.data, secrets.isLoading,
      evaluations.data, evaluations.isLoading
    ]
  );
}

type ResourcePickerProps = {
  kind: ResourceKind;
  selected: string[];
  onChange: ( next: string[] ) => void;
  disabled?: boolean;
};

export function ResourcePicker( { kind, selected, onChange, disabled }: ResourcePickerProps ) {
  const [ filter, setFilter ] = useState( '' );
  const { options, isLoading, hasMore } = useResourceOptions( kind, filter );

  const selectedSet = useMemo( () => new Set( selected ), [ selected ] );

  const toggle = useCallback( ( id: string ) => {
    if ( disabled ) return;
    onChange( selectedSet.has( id ) ? selected.filter( s => s !== id ) : [ ...selected, id ] );
  }, [ disabled, onChange, selected, selectedSet ] );

  // Select/deselect every row on the current page
  const togglePage = useCallback( ( pageIds: string[], allPageSelected: boolean ) => {
    if ( disabled ) return;
    if ( allPageSelected ) {
      const remove = new Set( pageIds );
      onChange( selected.filter( id => !remove.has( id ) ) );
    } else {
      onChange( [ ...new Set( [ ...selected, ...pageIds ] ) ] );
    }
  }, [ disabled, onChange, selected ] );

  const selectAllInWorkspace = useCallback( () => {
    if ( disabled ) return;
    onChange( options.map( o => o.id ) );
  }, [ disabled, onChange, options ] );

  const columns = useMemo( () => [
    columnHelper.display( {
      id: 'select',
      header: ( { table } ) => {
        const pageIds = table.getRowModel().rows.map( r => r.original.id );
        const allPageSelected = pageIds.length > 0 && pageIds.every( id => selectedSet.has( id ) );
        return (
          <input
            type="checkbox"
            checked={ allPageSelected }
            disabled={ disabled }
            onChange={ () => togglePage( pageIds, allPageSelected ) }
            aria-label="Select all on page"
            data-testid={ `resource-picker-${ kind }-all` }
          />
        );
      },
      cell: info => {
        const id = info.row.original.id;
        return (
          <input
            type="checkbox"
            checked={ selectedSet.has( id ) }
            disabled={ disabled }
            onChange={ () => toggle( id ) }
            data-testid={ `resource-picker-${ kind }-${ id }` }
          />
        );
      },
      size: 40
    } ),
    columnHelper.accessor( 'label', {
      header: () => (
        <span>
          { kind.charAt( 0 ).toUpperCase() + kind.slice( 1 ) }
          {' '}({ options.length }{ hasMore ? '+' : '' })
        </span>
      ),
      cell: info => <span>{ info.getValue() }</span>
    } )
  ], [ kind, options.length, hasMore, selectedSet, disabled, toggle, togglePage ] );

  const table = useNativeTable<Option>( {
    data: options,
    columns,
    enableSorting: false,
    initialState: { pagination: { pageSize: PAGE_SIZE } }
  } );

  const pageIds = table.getRowModel().rows.map( r => r.original.id );
  const allPageSelected = pageIds.length > 0 && pageIds.every( id => selectedSet.has( id ) );
  const allSelected = options.length > 0 && options.every( o => selectedSet.has( o.id ) );
  const showSelectAllBanner = allPageSelected && !allSelected && options.length > pageIds.length;

  if ( isLoading ) {
    return <div className={ styles.picker_empty }>Loading...</div>;
  }
  if ( options.length === 0 && !filter.trim() ) {
    return <div className={ styles.picker_empty }>No { kind }s available</div>;
  }

  return (
    <div className={ styles.picker } data-testid={ `resource-picker-${ kind }` }>
      <input
        type="text"
        className={ styles.filter_input }
        placeholder={ `Search ${ kind }s...` }
        value={ filter }
        onChange={ e => setFilter( e.target.value ) }
        disabled={ disabled }
        data-testid={ `resource-picker-${ kind }-filter` }
      />

      { showSelectAllBanner && (
        <div className={ styles.select_all_banner } data-testid={ `resource-picker-${ kind }-banner` }>
          All { pageIds.length } { kind }s on this page selected.{' '}
          <span
            className={ styles.select_all_link }
            role="button"
            tabIndex={ 0 }
            onClick={ selectAllInWorkspace }
            onKeyDown={ e => { if ( e.key === 'Enter' || e.key === ' ' ) selectAllInWorkspace(); } }
            data-testid={ `resource-picker-${ kind }-select-all-workspace` }
          >
            Select all { options.length }{ hasMore ? '+' : '' } { kind }s
            { hasMore && ` (showing first ${ BACKEND_LIMIT })` }.
          </span>
        </div>
      ) }

      { options.length === 0 ? (
        <div className={ styles.picker_empty }>No { kind }s match "{ filter.trim() }".</div>
      ) : (
        <NativeTable
          table={ table }
          pagination={ options.length > PAGE_SIZE ? 'client' : false }
          tbodyTestId={ `resource-picker-${ kind }-body` }
          rowClassName={ row => ( selectedSet.has( row.original.id ) ? styles.row_selected : undefined ) }
        />
      ) }

      { hasMore && (
        <div className={ styles.more_results_hint } data-testid={ `resource-picker-${ kind }-more` }>
          Warning: Showing first { BACKEND_LIMIT } results. Use search to find others.
        </div>
      ) }
    </div>
  );
}
